import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { PanelModule } from 'primeng/panel';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { Subject, takeUntil } from 'rxjs';
import { VersionHistoryService, VersionRelease } from '../services/version-history.service';

@Component({
    selector: 'app-version-history',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        CardModule,
        PanelModule,
        TagModule,
        InputTextModule,
        IconFieldModule,
        InputIconModule,
        ButtonModule,
        ToolbarModule
    ],
    templateUrl: './version-history.component.html',
    styleUrl: './version-history.component.scss'
})
export class VersionHistoryComponent implements OnInit, OnDestroy {
    currentVersion = '';
    latestProductVersion = '';
    searchTerm = '';
    releases: VersionRelease[] = [];
    filteredReleases: VersionRelease[] = [];
    loading = true;
    loadError = false;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private versionHistoryService: VersionHistoryService,
        private location: Location,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.versionHistoryService
            .getCurrentVersion()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (version) => {
                    this.currentVersion = version;
                    this.versionHistoryService.registerBuild(version).pipe(takeUntil(this.destroy$)).subscribe();
                },
                error: () => (this.currentVersion = '')
            });

        this.versionHistoryService
            .getReleases()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (releases) => {
                    this.releases = releases;
                    this.latestProductVersion = releases[0]?.version ?? '';
                    this.applyFilter();
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                    this.loadError = true;
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    goBack(): void {
        if (window.history.length > 1) {
            this.location.back();
        } else {
            this.router.navigate(['/view-profile']);
        }
    }

    onSearchChange(): void {
        this.applyFilter();
    }

    clearSearch(): void {
        this.searchTerm = '';
        this.applyFilter();
    }

    private applyFilter(): void {
        const term = this.searchTerm.trim().toLowerCase();
        if (!term) {
            this.filteredReleases = [...this.releases];
            return;
        }

        this.filteredReleases = this.releases.filter((release) => {
            const haystack = [
                release.version,
                release.title ?? '',
                release.goLiveDivision ?? '',
                release.period,
                release.summary,
                ...(release.features ?? []),
                ...(release.enhancements ?? []),
                ...(release.bugFixes ?? [])
            ]
                .join(' ')
                .toLowerCase();
            return haystack.includes(term);
        });
    }
}
